















































































// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';

// Third party
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { mask } from 'vue-the-mask';

// Constants
import Assets from '@/constants/assets';

// Directives
import numberField from '@/shared/directives/number-field.directive.ts';

export enum InputStyle {
  LabelTop = 'input--label-top',
  LabelLeft = 'input--label-left',
  WithoutLabel = 'input--without-label',
  WithBorder = 'input--with-border',
}

export enum InputSize {
  Md = 'input--size-md',
  Sm = 'input--size-sm',
  Xxl = 'input--size-xxl',
}

export enum InputPredefinedStyle {
  Datepicker = 'input--datepicker',
}

export enum InputLabelColor {
  BlueDark = 'input--label-blue-dark',
  GreyDark = 'input--label-grey-dark',
  Primary = 'input--label-primary',
}

export enum InputType {
  Text = 'text',
  Password = 'password',
  Number = 'number',
  Date = 'date',
}

export enum InputAlign {
  Left = 'input--left-align',
  Right = 'input--right-align',
  Center = 'input--center-align',
}

export enum InputTextWeigth {
  Black = 'input--fw-black',
}

@Component({
  components: {
    Icon,
  },
  directives: {
    mask,
    numberField,
  },
})
export default class Input extends Vue {
  @Prop({ default: '' }) private inputValue!: string;
  @Prop({ default: InputType.Text }) private type!: InputType;
  @Prop() private placeholder!: string;
  @Prop({ default: true }) private spellcheck!: boolean;
  @Prop() private inputLabel!: string;
  @Prop({ default: InputAlign.Left }) private align!: InputAlign;
  @Prop() private inputWeight!: InputTextWeigth;
  @Prop({ default: false }) private readOnly!: boolean;
  @Prop({ default: false }) private squad!: boolean;
  @Prop({ default: false }) private clearable!: boolean;
  @Prop([String, Array]) private mask!: string | Array<string>;
  @Prop({ default: '' }) private inputError!: string;
  @Prop({ default: InputStyle.LabelTop }) private inputStyle!: InputStyle;
  @Prop() private inputPredefinedStyle!: InputPredefinedStyle;
  @Prop({ default: InputLabelColor.BlueDark })
  private labelColor!: InputLabelColor;
  @Prop({ default: InputSize.Md }) private inputSize!: InputSize;
  @Prop({ default: '' }) private measure!: string;
  @Prop({ default: '' }) private maxValue!: string;

  private content: string = '';
  private dateInputLenght!: number;
  private isFocused: boolean = false;
  private inputTypeEnum = InputType;
  private iconEnum = IconEnum;

  @Watch('inputValue')
  private onInputValueChanged(newVal: string, oldVal: string) {
    this.setContent(newVal);
  }

  private created() {
    if (this.mask) {
      this.dateInputLenght = this.mask.length - 1;
    }
    if (this.inputValue) {
      this.setContent(this.inputValue, true);
    }
  }

  get showCloseIcon() {
    return this.clearable && !!this.content;
  }

  get hasFocus() {
    const focus = this.isFocused && !this.readOnly && this.checkStyle;
    this.checkShowMeasure(focus);
    return focus;
  }

  private reset() {
    this.content = '';
    this.$emit('input', this.content);
    this.$emit('value-changed', this.content);
  }

  get checkStyle() {
    return (
      InputStyle.LabelTop === this.inputStyle ||
      InputStyle.WithoutLabel === this.inputStyle
    );
  }

  get isNumberOrDate() {
    return this.type === InputType.Number || this.type === InputType.Date;
  }

  get inputType() {
    let result = '';
    if (this.type === InputType.Date || this.type === InputType.Number) {
      result = 'text';
    } else {
      result = this.type;
    }
    return result;
  }

  get dateChangeIsValid() {
    return (
      this.type === InputType.Date &&
      dayjs(this.content).isValid() &&
      this.content.length > this.dateInputLenght
    );
  }

  get isDateWithCorrectLenght() {
    return (
      this.type === InputType.Date && this.content.length > this.dateInputLenght
    );
  }

  get placeholderText() {
    return this.placeholder;
  }

  get hasError() {
    return this.inputError !== '';
  }

  get hasMeasure() {
    return this.measure !== '';
  }

  private handleFocus() {
    this.isFocused = true;
    this.$emit('on-focus');
  }

  private handleBlur() {
    this.isFocused = false;
    this.$emit('blur');
  }

  private handleKeyup(e: any) {
    this.$emit('keyup', e);
    this.hideError();
  }

  private handleClick() {
    this.$emit('click');
  }

  private handleInput(e: any) {
    let inputContent = '';
    if (this.isDateWithCorrectLenght) {
      const dateSplitted = this.content.split('/');
      let year = Number(dateSplitted[2]);
      if (String(year).length === 2) {
        year = Number('20' + year);
      }
      const month = Number(dateSplitted[1]);
      const day = Number(dateSplitted[0]);
      const formatDate = dayjs(`${year}-${month}-${day}`,  { locale: 'es' }).add(2, 'hour');
      if (formatDate.isValid()) {
        inputContent = formatDate.toISOString();
      }
    } else {
      inputContent = this.content;
    }
    inputContent = this.removeSpaces(inputContent);
    this.removeMeasure();
    this.$emit('value-changed', inputContent);
    this.$emit('input', inputContent);
  }

  private formatNumber() {
    const inputNumberValue = Number(this.inputValue);
    return inputNumberValue.toString();
  }

  private hideError() {
    this.$emit('hide-error');
  }

  private checkShowMeasure(focus: boolean) {
    if (this.hasMeasure) {
      if (focus) {
        this.content = this.content.replace(this.measure, '');
      } else if (
        !focus &&
        this.content.length > 0 &&
        !this.content.includes(this.measure)
      ) {
        this.content += this.measure;
      }
    }
  }

  private removeSpaces(value: string) {
    let cleanValue = value;

    if (this.mask && this.mask !== '') {
      cleanValue = value.replace(/\s/g, '');
    }

    return cleanValue;
  }

  private removeMeasure() {
    if (this.hasMeasure) {
      this.content = this.content.replace(this.measure, '');
    }
  }

  private setContent(value: any, created: any = false) {
    if (value !== '') {
      this.hideError();
    }
    this.content = value;
    if (this.dateChangeIsValid) {
      if (this.dateInputLenght > 8) {
        this.content = dayjs(this.content).format('DD/MM/YYYY');
      } else {
        const temp = this.content.split('/');
        temp[2] = '20' + temp[2];
        this.content = dayjs(`${temp[2]}-${temp[1]}-${temp[0]}`).format(
          'DD/MM/YY',
        );
      }
    } else if (this.type === InputType.Number && created) {
      this.content = this.formatNumber();
    }
  }
}
